import { useState, useEffect } from 'react';
import extend from 'lodash/extend';
import ResultsInfo from '@Services/ResultsInfo';
import Breadcrumb from '@Services/Breadcrumb';
import { recordCountOrTotalResultFromDatabaseItem } from '@Containers/PatentFamilyClustering/selectors';
import useSearchObjectFromStorage from './useSearchObjectFromStorage';

function getAvailableDatabases({ resultName, databaseId, activeBreadcrumb }) {
  const infoItems = ResultsInfo.getResults(resultName, databaseId) || [];
  let availableDbs = [];

  infoItems.forEach((item) => {
    const isLoading = !item.canceled && item.totalResult === undefined;
    extend(item, {
      title: `${item.dbName} - `,
      isLoading,
      ...(!isLoading && {
        count: recordCountOrTotalResultFromDatabaseItem(item),
      }),
    });
  });

  // RX-19851 - don't show database switcher after context switch
  availableDbs = infoItems.filter(({ context }) => context === activeBreadcrumb.context);

  return availableDbs;
}

// Further refactor note: be careful with effect deps.
export default function useAvailableDatabases({ searchId, resultName, databaseId }, activeBreadcrumb) {
  const [availableDatabases, setAvailableDatabases] = useState([]);
  const [searchObject, searchObjectReady] = useSearchObjectFromStorage({ searchId });
  const currentDatabase = availableDatabases.find(
    (item) => item.databaseId != null && databaseId != null && String(item.databaseId) === String(databaseId)
  );
  const rootBreadcrumb = Breadcrumb.retrieveRootBreadcrumb(databaseId);
  const allowedContexts = ['substances', 'reactions', 'citations'];
  const isDbSwitcherVisible = !!(
    rootBreadcrumb &&
    allowedContexts.includes(rootBreadcrumb.context) &&
    currentDatabase &&
    availableDatabases.length > 1
  );

  useEffect(() => {
    let delayedDataSubscription;
    if (searchObjectReady) {
      setAvailableDatabases(getAvailableDatabases({ searchObject, activeBreadcrumb, resultName, databaseId }));

      // Subscribe for delayed data to update database switcher
      delayedDataSubscription = ResultsInfo.delayedResultsInfoSubject.subscribe(() => {
        setAvailableDatabases(getAvailableDatabases({ searchObject, activeBreadcrumb, resultName, databaseId }));
      });
    }

    return () => {
      if (delayedDataSubscription) {
        delayedDataSubscription.unsubscribe();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchObjectReady, activeBreadcrumb]);

  return {
    availableDatabases,
    currentDatabase,
    isDbSwitcherVisible,
  };
}
